/**
 * useAnalyticsQuery — Sprint 3.7.
 *
 * Composes the V2 /api/analytics/* endpoints into a single hook the Pro
 * Analytics page can consume. Builds the request payload (mirrors V2's shape
 * including the legacy `concessionareId` field name), debounces refetches,
 * and exposes a `runDrilldown` helper for the slide-in panel.
 *
 *   const {
 *     locationsTree, locationsLoading,
 *     metrics, fbMetrics, current, prior,
 *     loading, error, refetch, runDrilldown,
 *   } = useAnalyticsQuery({
 *     concessionaireId, propertyAccess,
 *     selectedType, selectedIds,         // location filter
 *     metricsFlags,                       // { sales:1, transCount:1, ... }
 *     groupBy, dateRange,                 // { from, to } ISO YYYY-MM-DD
 *   });
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v3Api } from '../hooks/useApi';

const DEBOUNCE_MS = 250;

export function useAnalyticsQuery(args) {
    const { t } = useTranslation();
    const {
        concessionaireId,
        propertyAccess,
        selectedType,
        selectedIds,
        metricsFlags,
        groupBy,
        dateRange,
        compareRange, // optional — when provided, overrides auto prior-period
    } = args;

    // ── Location tree ────────────────────────────────────────────────
    const [locationsTree, setLocationsTree] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationsError, setLocationsError] = useState(null);

    useEffect(() => {
        if (!concessionaireId) return;
        let cancelled = false;
        setLocationsLoading(true);
        v3Api.analytics
            .location({ concessionaire_id: concessionaireId, property_access: propertyAccess })
            .then((data) => {
                if (cancelled) return;
                setLocationsTree(transformLocationTree(data));
                setLocationsError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setLocationsError(err?.response?.data?.error || err?.message || t('Failed to load locations'));
            })
            .finally(() => {
                if (cancelled) return;
                setLocationsLoading(false);
            });
        return () => { cancelled = true; };
    }, [concessionaireId, propertyAccess, t]);

    // ── Main + prior-period query ────────────────────────────────────
    const [metrics, setMetrics] = useState([]);
    const [fbMetrics, setFbMetrics] = useState([]);
    const [priorMetrics, setPriorMetrics] = useState([]);
    const [priorRange, setPriorRange] = useState(null);
    const [current, setCurrent] = useState(null);
    const [prior, setPrior] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const runIdRef = useRef(0);
    const debounceRef = useRef(null);

    const buildSingleBody = useCallback((overrides = {}) => {
        const m = metricsFlags || {};
        return {
            query: {
                concessionareId: concessionaireId,
                locations: [
                    {
                        type: selectedType || 'Concessionaire',
                        values: selectedIds && selectedIds.length ? selectedIds : (concessionaireId ? [concessionaireId] : []),
                    },
                ],
                metrics: [{
                    trafficIn: 0,
                    trafficOut: 0,
                    sales: m.sales || 0,
                    transCount: m.transCount || 0,
                    conversionRate: 0,
                    atv: m.atv || 0,
                    fb: m.fb || 0,
                    revenueShareAmount: m.revenueShareAmount || 0,
                    revenueSharePercentage: m.revenueSharePercentage || 0,
                    baseRent: m.baseRent || 0,
                }],
                dateRanges: [{
                    start: overrides.from || dateRange?.from,
                    finish: overrides.to || dateRange?.to,
                }],
                timeRanges: [{ type: 'store_hours' }],
                groupBy: [overrides.groupBy || groupBy || 'date'],
            },
        };
    }, [concessionaireId, selectedType, selectedIds, metricsFlags, dateRange, groupBy]);

    const reqKey = useMemo(() => JSON.stringify({
        concessionaireId, selectedType, selectedIds, metricsFlags, groupBy, dateRange, compareRange,
    }), [concessionaireId, selectedType, selectedIds, metricsFlags, groupBy, dateRange, compareRange]);

    const runQuery = useCallback(async () => {
        if (!concessionaireId) return;
        if (!dateRange?.from || !dateRange?.to) return;

        const myRun = ++runIdRef.current;
        setLoading(true);
        setError(null);

        try {
            // Main grouped query for chart + table
            const main = await v3Api.analytics.single(buildSingleBody());
            if (myRun !== runIdRef.current) return;

            const rows = main?.metrics || [];
            const fbRows = main?.fbMetrics || [];
            setMetrics(rows);
            setFbMetrics(fbRows);

            // Totals (current period) — sum from grouped rows; cheap & avoids extra round-trip
            const sums = sumRows(rows);
            setCurrent(sums);

            // Compare range: caller-provided takes precedence, else auto-shift
            // the active range backwards by its own span. Group by the SAME
            // field as current so the rows can be overlaid when the user
            // toggles the compare overlay on. Totals (KPI delta) are derived
            // by summing the rows — works regardless of groupBy.
            const priorR = (compareRange && compareRange.from && compareRange.to)
                ? compareRange
                : shiftRangeBackwards(dateRange);
            setPriorRange(priorR);
            if (priorR) {
                try {
                    const priorRes = await v3Api.analytics.single(buildSingleBody({
                        from: priorR.from, to: priorR.to, groupBy: groupBy || 'date',
                    }));
                    if (myRun !== runIdRef.current) return;
                    const priorRows = priorRes?.metrics || [];
                    setPriorMetrics(priorRows);
                    setPrior(sumRows(priorRows));
                } catch (e) {
                    setPriorMetrics([]);
                    setPrior(null); // non-fatal: KPI strip will hide deltas
                }
            } else {
                setPriorMetrics([]);
                setPrior(null);
            }
        } catch (err) {
            if (myRun !== runIdRef.current) return;
            setError(err?.response?.data?.error || err?.message || t('Failed to load analytics'));
        } finally {
            if (myRun === runIdRef.current) setLoading(false);
        }
    }, [concessionaireId, dateRange, groupBy, compareRange, buildSingleBody, t]);

    // Debounced auto-refetch when inputs change
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { runQuery(); }, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reqKey]);

    const runDrilldown = useCallback(async ({ groupByValue, dateOverride }) => {
        // Drill: same filters, but force groupBy=tenant and (when bucket is a date)
        // collapse the range to that single day. Returns { totals, rows } sorted desc.
        const overrides = { groupBy: 'tenant' };
        if (dateOverride) {
            overrides.from = dateOverride;
            overrides.to = dateOverride;
        }
        const res = await v3Api.analytics.single(buildSingleBody(overrides));
        const rows = (res?.metrics || []).slice().sort((a, b) => (b.sales || 0) - (a.sales || 0));
        return {
            label: dateOverride || groupByValue || t('Drill-down'),
            totals: sumRows(rows),
            rows,
        };
    }, [buildSingleBody, t]);

    return {
        locationsTree,
        locationsLoading,
        locationsError,
        metrics,
        fbMetrics,
        priorMetrics,
        priorRange,
        current,
        prior,
        loading,
        error,
        refetch: runQuery,
        runDrilldown,
    };
}

/* ───────────── helpers ───────────── */

// Backend returns [{ concessionaire: { id, name, countries: [...] } }, ...]
function transformLocationTree(data) {
    if (!Array.isArray(data)) return [];
    return data.map(item => ({
        id: item?.concessionaire?.id,
        text: item?.concessionaire?.name,
        level: 0,
        type: 'Concessionaire',
        children: (item?.concessionaire?.countries || []).map(country => ({
            id: country.id, text: country.name, level: 1, type: 'Country',
            children: (country.properties || []).map(property => ({
                id: property.id, text: property.name, level: 2, type: 'Property',
                children: (property.tenants || []).map(tenant => ({
                    id: tenant.id, text: tenant.name, level: 3, type: 'Tenant',
                })).sort(byText),
            })).sort(byText),
        })).sort(byText),
    })).sort(byText);
}

function byText(a, b) { return String(a.text || '').localeCompare(String(b.text || '')); }

function sumRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { sales: 0, transactionCount: 0, atv: 0, revenueShareAmount: 0, baseRent: 0 };
    }
    const acc = rows.reduce((a, r) => ({
        sales:               a.sales               + numOrZero(r.sales),
        transactionCount:    a.transactionCount    + numOrZero(r.transactionCount),
        revenueShareAmount:  a.revenueShareAmount  + numOrZero(r.revenueShareAmount),
        baseRent:            a.baseRent            + numOrZero(r.baseRent),
    }), { sales: 0, transactionCount: 0, revenueShareAmount: 0, baseRent: 0 });
    acc.atv = acc.transactionCount > 0 ? acc.sales / acc.transactionCount : 0;
    return acc;
}

function numOrZero(v) {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
}

// Given { from, to } as 'YYYY-MM-DD', shift backwards by the same span
// (inclusive day count). Returns { from, to } strings or null if invalid.
export function shiftRangeBackwards(range) {
    if (!range || !range.from || !range.to) return null;
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    const oneDay = 86400000;
    const span = Math.max(0, Math.round((to - from) / oneDay));
    const priorTo = new Date(from.getTime() - oneDay);
    const priorFrom = new Date(priorTo.getTime() - span * oneDay);
    return { from: fmtDate(priorFrom), to: fmtDate(priorTo) };
}

// Shift a {from,to} range backwards by `years` calendar years.
export function shiftYearsBack(range, years = 1) {
    if (!range || !range.from || !range.to) return null;
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    from.setFullYear(from.getFullYear() - years);
    to.setFullYear(to.getFullYear() - years);
    return { from: fmtDate(from), to: fmtDate(to) };
}

// Shift a {from,to} range backwards by `months` calendar months.
export function shiftMonthsBack(range, months = 1) {
    if (!range || !range.from || !range.to) return null;
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    from.setMonth(from.getMonth() - months);
    to.setMonth(to.getMonth() - months);
    return { from: fmtDate(from), to: fmtDate(to) };
}

function fmtDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
